import { AlertTriangle, Activity, Users, Clock, Zap, Radio } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { DashboardStats } from '../../types';

interface StatsBarProps {
  stats: DashboardStats;
  layout?: 'horizontal' | 'grid' | 'compact';
  className?: string;
}

const statDefs = [
  { key: 'activeIncidents' as const, label: 'Active', icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', pulse: true },
  { key: 'totalIncidentsToday' as const, label: 'Today', icon: Activity, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { key: 'availableUnits' as const, label: 'Available', icon: Users, color: 'text-green-400', bg: 'bg-green-500/10' },
  { key: 'deployedUnits' as const, label: 'Deployed', icon: Radio, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { key: 'avgResponseTime' as const, label: 'Avg Resp', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', suffix: 'm' },
  { key: 'criticalAlerts' as const, label: 'Critical', icon: Zap, color: 'text-red-400', bg: 'bg-red-500/10' },
];

export default function StatsBar({ stats, layout = 'horizontal', className }: StatsBarProps) {
  if (layout === 'compact') {
    return (
      <div className={cn('flex items-center gap-4 px-3 py-1.5 text-xs', className)}>
        {statDefs.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <s.icon className={cn('w-3 h-3', s.color, s.pulse && 'animate-pulse')} />
            <span className="text-muted-foreground">{s.label}:</span>
            <span className={cn('font-mono font-bold', s.color)}>
              {stats[s.key]}{s.suffix || ''}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (layout === 'grid') {
    return (
      <div className={cn('grid grid-cols-3 xl:grid-cols-6 gap-2', className)}>
        {statDefs.map((s) => (
          <div key={s.key} className="glass-panel p-2">
            <div className="flex items-center gap-2">
              <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0', s.bg)}>
                <s.icon className={cn('w-3.5 h-3.5', s.color, s.pulse && 'animate-pulse')} />
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-foreground leading-tight">
                  {stats[s.key]}{s.suffix || ''}
                </p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // horizontal (ribbon)
  return (
    <div className={cn('flex items-center gap-3 px-4 py-2 border-b border-border bg-card', className)}>
      {statDefs.map((s) => (
        <div key={s.key} className="flex items-center gap-1.5">
          <div className={cn('w-6 h-6 rounded flex items-center justify-center', s.bg)}>
            <s.icon className={cn('w-3 h-3', s.color, s.pulse && 'animate-pulse')} />
          </div>
          <div>
            <p className="text-xs font-bold text-foreground leading-tight">
              {stats[s.key]}{s.suffix || ''}
            </p>
            <p className="text-[9px] text-muted-foreground">{s.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
